import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Grid, useMediaQuery } from '@mui/material';
import { useDeepCompareEffect } from '@fuse/hooks';
import ReactFlow, { Background, Controls, useEdgesState, useNodesState, type Node } from 'reactflow';
import 'reactflow/dist/style.css';

import { ErrorAlert, Utils } from 'app/main/components';
import wbsSettings from './WbsGraphSettings';
import nodeTypes from './WbsNodeTypes';
import useWbsState, { type WbsNode, type WbsRootNode } from './useWbsState';
import WbsGraphMenu from './WbsGraphMenu';
import WbsNodeViewer from './WbsNodeViewer';

interface WbsGraphViewerProps {
	root: WbsRootNode;
}

const { rootPosition, snapGrid, defaultViewport } = wbsSettings;

export default function WbsGraphViewer({ root }: WbsGraphViewerProps) {
	const dispatch = useDispatch();
	const isTablet = useMediaQuery(theme => theme.breakpoints.down('md'));

	const [wbsNodes, setWbsNodes] = React.useState([]);
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState<any>();
	const [refresh, setRefresh] = React.useState(false);
	const updateRefresh = () => setRefresh(state => !state);

	const [nodeViewerRefresh, setNodeViewerRefresh] = React.useState(0);
	const updateNodeViewerRefresh = React.useCallback(() => setNodeViewerRefresh(state => state + 1), []);

	const [selectedNode, setSelectedNode] = React.useState<Node<WbsRootNode | WbsNode>>();

	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);

	const [rootNode] = React.useState({ ...root, position: rootPosition });

	const { mapWbsNodes, onSelectionChange } = useWbsState({
		rootNode,
		setNodes,
		setEdges,
		setSelectedNode,
		updateNodeViewerRefresh
	});

	useDeepCompareEffect(() => {
		setNodes([]);
		setEdges([]);
		mapWbsNodes({ nodes: wbsNodes, parentId: 'root', offset: { ...rootPosition } });
	}, [mapWbsNodes, refresh, setEdges, setNodes, wbsNodes]);

	React.useEffect(() => {
		setIsLoading(true);

		Utils.executeStored({
			action: 'st_wbs_nodes_list',
			params: { wbsn_table: root.table, wbsn_table_uid: root.tableId },
			dispatch,
			successHandler: response => {
				setWbsNodes(response.data || []);
				setIsLoading(false);
			},
			errorHandler: ex => {
				setError(ex);
				setIsLoading(false);
			}
		});
	}, [dispatch, refresh, root.table, root.tableId]);

	return (
		<Grid className="h-full" container justifyItems="stretch" spacing={2}>
			<Grid size={{ md: 8, xs: 12 }}>
				<Box
					className="h-full w-full rounded-8"
					sx={theme => ({
						border: `1px solid ${theme.palette.divider}`
					})}
				>
					{error ? (
						<ErrorAlert className="h-full rounded-8" error={error} />
					) : (
						<ReactFlow
							className="w-full"
							nodeTypes={nodeTypes}
							nodes={nodes}
							edges={edges}
							onNodesChange={onNodesChange}
							onEdgesChange={onEdgesChange}
							onSelectionChange={onSelectionChange}
							snapGrid={snapGrid}
							snapToGrid
							defaultViewport={defaultViewport}
							// fitView
							nodesConnectable={false}
							nodesDraggable={false}
							zoomOnDoubleClick={false}
							panOnScroll
							// elementsSelectable={false}
							attributionPosition="bottom-left"
							proOptions={{ hideAttribution: true }}
						>
							<Controls showFitView={false} showInteractive={false} />
							<Background />
							{isLoading && <CircularProgress className="absolute right-0 top-0 m-12" disableShrink />}
						</ReactFlow>
					)}
				</Box>
			</Grid>
			{!isTablet && !selectedNode && (
				<Grid size={4}>
					<WbsGraphMenu root={root} nodes={nodes} updateRefresh={updateRefresh} />
				</Grid>
			)}
			{selectedNode && (
				<WbsNodeViewer
					key={nodeViewerRefresh}
					root={root}
					selectedNode={selectedNode}
					setSelectedNode={setSelectedNode}
					updateRefresh={updateRefresh}
				/>
			)}
		</Grid>
	);
}
